<template>
  <div>
    <!-- needed since v-navigation-drawer is on top of another drawer -->
    <v-overlay
      :value="value"
      :z-index="250"
      data-testid="aggregate-learning-leaderboard-user-profile-overlay"
      @click="closeSheet"
    />
    <v-navigation-drawer
      :value="value"
      temporary
      fixed
      right
      width="500"
      hide-overlay
      :style="{ zIndex: 251 }"
      data-testid="aggregate-learning-leaderboard-user-profile-sheet"
      @input="$emit('input', $event)"
      class="text-left"
    >
      <DrawerToolbar
        :title="toolbarTitle"
        show-back-button
        :show-close-button="false"
        @back="closeSheet"
      />

      <div
        v-if="loading"
        class="d-flex justify-center pa-6"
        data-testid="aggregate-learning-leaderboard-user-profile-loading"
      >
        <v-progress-circular indeterminate :color="resolvedAccentColor" />
      </div>

      <template v-else-if="profile">
        <div class="d-flex align-center pa-4" :style="{ gap: '8px' }">
          <img
            v-if="showAvatarImage"
            :src="profile.user.avatarUrl"
            :alt="profile.user.displayName"
            class="rounded-circle flex-shrink-0"
            :style="avatarImageStyle"
            @error="onAvatarError"
          />
          <div
            v-else
            class="d-flex align-center justify-center rounded-circle flex-shrink-0"
            :style="initialsAvatarStyle"
          >
            <Typography variant="subtitle-1" :style="{ color: 'white' }">
              {{ profile.user.initials }}
            </Typography>
          </div>
          <div class="d-flex flex-column" :style="{ minWidth: 0, flex: '1 1 0' }">
            <Typography variant="subtitle-1" class="text-truncate">
              {{ profile.user.displayName }}
            </Typography>
            <Typography v-if="subtitleText" variant="body-1" class="text-truncate">
              {{ subtitleText }}
            </Typography>
          </div>
        </div>

        <v-divider />

        <div class="d-flex flex-column pa-4" :style="{ gap: '24px' }">
          <div class="d-flex align-center" :style="{ gap: '24px' }">
            <Typography variant="h-5" :style="{ flex: '1 1 0', minWidth: 0 }">
              Programs
            </Typography>
            <Typography variant="subtitle-1" class="mediumGrey--text">
              {{ programsCount }}
            </Typography>
          </div>

          <div
            v-if="programsCount === 0"
            class="mediumGrey--text"
            data-testid="aggregate-learning-leaderboard-user-profile-programs-empty"
          >
            <Typography variant="body-1">This user is not enrolled in any programs yet.</Typography>
          </div>
          <div v-else class="d-flex flex-column" :style="{ gap: '24px' }">
            <Typography
              v-for="program in displayedPrograms"
              :key="program.programId"
              variant="body-1"
              :data-testid="`aggregate-learning-leaderboard-user-profile-program-${program.programId}`"
            >
              {{ program.displayName }}
            </Typography>

            <a
              v-if="hasMorePrograms"
              class="d-flex align-center"
              :style="{ gap: '8px', cursor: 'pointer' }"
              data-testid="aggregate-learning-leaderboard-user-profile-programs-toggle"
              @click="toggleProgramsExpanded"
            >
              <Typography
                variant="subtitle-1"
                :style="{ flex: '1 1 0', minWidth: 0, color: '#000' }"
              >
                {{ programsExpanded ? "Show less" : "See all" }}
              </Typography>
              <Icon :icon="programsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
            </a>
          </div>

          <v-divider />

          <div class="d-flex align-center" :style="{ gap: '24px' }">
            <Typography variant="h-5" :style="{ flex: '1 1 0', minWidth: 0 }"> Badges </Typography>
            <Typography variant="subtitle-1" class="mediumGrey--text">
              {{ badgesCount }}
            </Typography>
          </div>

          <div
            v-if="badgesCount === 0"
            class="mediumGrey--text"
            data-testid="aggregate-learning-leaderboard-user-profile-badges-empty"
          >
            <Typography variant="body-1">This user has not earned any badges yet.</Typography>
          </div>
          <div v-else class="badges-grid">
            <div
              v-for="badge in displayedBadges"
              :key="badge.badgeId"
              class="d-flex flex-column align-center"
              :style="{ gap: '8px' }"
              :data-testid="`aggregate-learning-leaderboard-user-profile-badge-${badge.badgeId}`"
            >
              <img
                :src="badge.imageUrl || fallbackBadgeImage"
                :alt="badge.displayName"
                class="rounded-circle"
                :style="badgeImageStyle"
                @error="onBadgeImageError"
              />
              <Typography variant="body-2" class="text-center">
                {{ badge.displayName }}
              </Typography>
            </div>
          </div>

          <a
            v-if="hasMoreBadges"
            class="d-flex align-center"
            :style="{ gap: '8px', cursor: 'pointer' }"
            data-testid="aggregate-learning-leaderboard-user-profile-badges-toggle"
            @click="toggleBadgesExpanded"
          >
            <Typography variant="subtitle-1" :style="{ flex: '1 1 0', minWidth: 0, color: '#000' }">
              {{ badgesExpanded ? "Show less" : "See all" }}
            </Typography>
            <Icon :icon="badgesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
          </a>

          <div class="d-flex justify-end">
            <Button
              :color="resolvedAccentColor"
              data-testid="aggregate-learning-leaderboard-user-profile-close-btn"
              @click="closeSheet"
            >
              Close
            </Button>
          </div>
        </div>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Icon from "@/components/generic/Icon.vue";
import Button from "@/components/generic/Button.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import activeClientMixin from "@/mixins/activeClientMixin";
import AggregateLeaderboardService from "@/services/AggregateLeaderboardService";

const COLLAPSED_PROGRAMS_COUNT = 5;
const COLLAPSED_BADGES_COUNT = 6;

export default {
  name: "AggregateLearningLeaderboardUserProfileSheet",
  mixins: [activeClientMixin],
  components: {
    Typography,
    Icon,
    Button,
    DrawerToolbar,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: String,
      default: null,
    },
    accentColor: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      profile: null,
      loading: false,
      programsExpanded: false,
      badgesExpanded: false,
      avatarImageFailed: false,
      leaderboardService: new AggregateLeaderboardService(),
    };
  },
  computed: {
    leaderboardClientId() {
      return this.userProfile?.clientId ?? null;
    },
    themeAccentColor() {
      return this.$vuetify?.theme?.themes?.light?.brandPrimary;
    },
    resolvedAccentColor() {
      return this.accentColor || this.themeAccentColor || "#077DAF";
    },
    resolvedAvatarBgColor() {
      return this.$vuetify?.theme?.themes?.light?.brandPrimary;
    },
    toolbarTitle() {
      return this.profile?.user?.displayName || "";
    },
    subtitleText() {
      const org = this.profile?.user?.organization;
      const title = this.profile?.user?.jobTitle;
      return [org, title].filter((v) => !!v).join(", ");
    },
    showAvatarImage() {
      return !!this.profile?.user?.avatarUrl && !this.avatarImageFailed;
    },
    avatarImageStyle() {
      return { width: "40px", height: "40px", objectFit: "cover" };
    },
    initialsAvatarStyle() {
      return {
        backgroundColor: this.resolvedAvatarBgColor,
        width: "40px",
        height: "40px",
      };
    },
    badgeImageStyle() {
      return { width: "96px", height: "96px", objectFit: "cover" };
    },
    fallbackBadgeImage() {
      return require("@/assets/Demo/Trophy_Badge.png");
    },
    programs() {
      return Array.isArray(this.profile?.programs) ? this.profile.programs : [];
    },
    badges() {
      return Array.isArray(this.profile?.badges) ? this.profile.badges : [];
    },
    programsCount() {
      return this.programs.length;
    },
    badgesCount() {
      return this.profile?.user?.badgesEarned ?? 0;
    },
    hasMorePrograms() {
      return this.programsCount > COLLAPSED_PROGRAMS_COUNT;
    },
    hasMoreBadges() {
      return this.badgesCount > COLLAPSED_BADGES_COUNT;
    },
    displayedPrograms() {
      if (this.programsExpanded || !this.hasMorePrograms) {
        return this.programs;
      }
      return this.programs.slice(0, COLLAPSED_PROGRAMS_COUNT);
    },
    displayedBadges() {
      if (this.badgesExpanded || !this.hasMoreBadges) {
        return this.badges;
      }
      return this.badges.slice(0, COLLAPSED_BADGES_COUNT);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.fetchProfile();
      } else {
        this.resetState();
      }
    },
    userId(newId, oldId) {
      if (this.value && newId && newId !== oldId) {
        this.fetchProfile();
      }
    },
  },
  methods: {
    closeSheet() {
      this.$emit("input", false);
    },
    onAvatarError() {
      this.avatarImageFailed = true;
    },
    onBadgeImageError(event) {
      const img = event?.target;
      if (!img) return;
      const fallback = this.fallbackBadgeImage;
      if (img.src !== fallback) {
        img.src = fallback;
      }
    },
    toggleProgramsExpanded() {
      this.programsExpanded = !this.programsExpanded;
    },
    toggleBadgesExpanded() {
      this.badgesExpanded = !this.badgesExpanded;
    },
    resetState() {
      this.profile = null;
      this.programsExpanded = false;
      this.badgesExpanded = false;
      this.avatarImageFailed = false;
    },
    async fetchProfile() {
      if (this.leaderboardClientId == null || !this.userId) return;
      this.loading = true;
      this.profile = null;
      this.avatarImageFailed = false;
      try {
        const response = await this.leaderboardService.getLeaderboardUserProfile(
          this.leaderboardClientId,
          this.userId
        );
        this.profile = response?.result ?? response ?? null;
      } catch (e) {
        console.log("Failed to fetch leaderboard user profile", e);
        this.profile = null;
        this.$store?.dispatch("showError", {
          message: "Failed to load user profile. Please try again later.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.badges-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 48px;
  row-gap: 24px;
}
</style>
